/**
 * Country Selector Form - Custom Element
 * Handles the custom currency/country picker in the header and footer.
 *
 * Safari / WebKit compatibility notes:
 *  - No arrow-function class fields (unsupported in older Safari CE lifecycle).
 *  - No optional-chaining on method calls (?.submit()) for Safari < 14.
 *  - Null-checks on every DOM query to guard against elements not yet parsed.
 *  - Registered under its own tag name ('country-selector-form') so it does
 *    NOT conflict with the Dawn-theme 'localization-form' custom element used
 *    by the header-drawer, announcement-bar, and footer localization widgets.
 */
class CountrySelectorForm extends HTMLElement {
  constructor() {
    super()

    this.elements = {
      input: this.querySelector('input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('ul')
    }

    // Bail out safely if critical elements are missing (Safari may call the
    // constructor before children are parsed in some edge-cases).
    if (!this.elements.button || !this.elements.input) return

    // Toggle dropdown when button clicked
    this.elements.button.addEventListener('click', this.toggleSelector.bind(this))

    // Close dropdown when clicking outside
    document.addEventListener('click', function (event) {
      if (!this.contains(event.target)) {
        this.closeSelector()
      }
    }.bind(this))

    // Handle list item click
    var links = this.querySelectorAll('a')
    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', this.onItemClick.bind(this))
    }

    this.userLoc = {
      code: localStorage.getItem('userLoc'),
      autoSet: localStorage.getItem('userLocSet'),
      error: localStorage.getItem('userLocError'),
      manuallySet: localStorage.getItem('manualLoc')
    }

    // Prevent auto-locate in certain cases
    if (
      !this.storageSupported() ||
      this.hasAttribute('footer-selector') ||
      this.userLoc.manuallySet ||
      this.userLoc.autoSet ||
      this.userLoc.error ||
      navigator.userAgent.indexOf('Firefox') > -1
    ) return

    // this.checkUserLoc()
  }

  checkUserLoc() {
    if (this.userLoc.code) return

    var _this = this
    var getIpCountry = function (url) {
      return fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('Error setting location')
          return response.json()
        })
        .then(function (payload) {
          if (!payload.location || !payload.location.country || !payload.location.country.code) return false
          _this.setStorage('userLoc', payload.location.country.code)
          return payload.location.country.code
        })
        .catch(function (e) {
          console.error(e)
          _this.setStorage('userLocError', true)
          return false
        })
    }

    getIpCountry('https://api.ipregistry.co/?key=g3mdoocee2bfuakp')
      .then(function (code) {
        if (!code) return
        _this.setLocInput(code, _this.elements.input.value)
      })
  }

  /**
   * Converted from arrow-function class field to a regular prototype method
   * so Safari (all versions) can handle it inside a custom element.
   */
  setLocInput(user, input) {
    var groups = window.theme && window.theme.locations
    if (!groups) return
    if (input === user) return

    // EU market countries (CH – Switzerland also routes to Global, not EU)
    var euMarketCodes = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES']


    if (
      groups.US.includes(user) ||
      groups.GB.includes(user) ||
      groups.CA.includes(user) ||
      groups.AU.includes(user) ||
      groups.JP.includes(user)
    ) {
      this.elements.input.value = user
    } else if (euMarketCodes.indexOf(user) !== -1) {
      // Route all EU market countries (including SE and CH) to the EU market (FR)
      this.elements.input.value = 'FR'
    } else {
      this.elements.input.value = 'BR'
    }

    this.setStorage('userLocSet', true)
        // Preserve the currently selected variant ID across market switches
    this.saveSelectedVariant()
    var form = this.querySelector('form')
    if (form) form.submit()
  }

  onItemClick(event) {
    event.preventDefault()
    this.elements.input.value = event.currentTarget.dataset.value
    this.setStorage('manualLoc', true)
        // Preserve the currently selected variant ID across market switches
    this.saveSelectedVariant()

    var form = this.querySelector('form')
    if (form) form.submit()
  }



  /**
   * Save the currently selected variant ID to localStorage before market switch.
   * This prevents auto-switching to a different variant when the selected one
   * is sold out or unavailable in the new market.
   */
  saveSelectedVariant() {
    try {
      // Try to get variant ID from URL params first
      var urlParams = new URLSearchParams(window.location.search)
      var variantId = urlParams.get('variant')

      // Fallback: get from the product form's hidden input
      if (!variantId) {
        var variantInput = document.querySelector('product-form input[name="id"], input.product-variant-id')
        if (variantInput) variantId = variantInput.value
      }

      // Fallback: get from variant-selects data
      if (!variantId) {
        var selectedVariantEl = document.querySelector('variant-selects [data-selected-variant]')
        if (selectedVariantEl) {
          try {
            var variantData = JSON.parse(selectedVariantEl.textContent)
            if (variantData && variantData.id) variantId = String(variantData.id)
          } catch (e) { /* ignore parse errors */ }
        }
      }

      if (variantId) {
        localStorage.setItem('preservedVariantId', variantId)
        // Also save the product handle/URL so we only restore for the same product
        var productUrl = document.querySelector('product-info')
        if (productUrl && productUrl.dataset.url) {
          localStorage.setItem('preservedVariantProductUrl', productUrl.dataset.url)
        }
      }
    } catch (e) {
      console.error('[CountrySelector] Error saving variant:', e)
    }
  }

  toggleSelector() {
    var expanded = this.elements.button.getAttribute('aria-expanded') === 'true'
    if (expanded) {
      this.closeSelector()
    } else {
      this.openSelector()
    }
  }

  openSelector() {
    this.elements.button.setAttribute('aria-expanded', 'true')
    if (this.elements.panel) {
      this.elements.panel.removeAttribute('hidden')
    }
  }

  closeSelector() {
    this.elements.button.setAttribute('aria-expanded', 'false')
    if (this.elements.panel) {
      this.elements.panel.setAttribute('hidden', true)
    }
  }

  storageSupported() {
    try {
      return ('localStorage' in window && window['localStorage'] !== null)
    } catch (e) {
      console.error(e)
      return false
    }
  }

  setStorage(key, value) {
    return localStorage.setItem(key, value)
  }
}

if (!customElements.get('country-selector-form')) {
  customElements.define('country-selector-form', CountrySelectorForm)
}
